// Public landing — non-signed-in visitors at "/".

import React from 'react';
import { Link } from 'react-router-dom';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import floobizMark from '../assets/floobiz-mark.jpg';
import floobizHero from '../assets/floobiz-hero.jpg';
import './Landing.css';

const SITE_NAME = process.env.REACT_APP_SITE_NAME || 'Floobiz';
const TOKEN_SYMBOL = process.env.REACT_APP_TOKEN_SYMBOL || 'FBIZ';

const CHAPTERS = [
  {
    num: '01',
    emoji: '🧪',
    title: 'The Worst Idea Ever',
    lead: 'It started with Mr. Floobiz — a blue, slightly confused creature in a wrinkled suit.',
    body: 'He didn\'t set out to build a revolution. He didn\'t even understand what a blockchain was. He just wanted to start a business. So he did what any "visionary" would do: printed a logo, bought a domain, launched a token. No plan. No purpose. Just vibes.',
  },
  {
    num: '02',
    emoji: '📉',
    title: 'Total Failure',
    lead: 'From day one, everything went wrong.',
    body: 'The website barely worked. The roadmap made no sense. The announcements contradicted each other. Mr. Floobiz bought at the top, sold at the bottom, and reinvested in worse ideas. Everyone said the same thing: "This won\'t last."',
  },
  {
    num: '03',
    emoji: '📈',
    title: 'Something Broke (Not How You Think)',
    lead: 'Instead of collapsing… Floobiz started growing.',
    body: 'Every bad move created attention. Every mistake turned into a meme. Every failure attracted more people. Because they saw something familiar — themselves. Not perfect traders. Not crypto experts. Just humans navigating chaos.',
  },
  {
    num: '04',
    emoji: '👔',
    title: 'The CEO Who Shouldn\'t Win',
    lead: 'Mr. Floobiz became a symbol.',
    body: 'He showed up late. Made the wrong calls. Celebrated losses like wins. And somehow… he kept winning. Not because he was smart — but because he never pretended to be. "If nothing makes sense… anything can work."',
  },
  {
    num: '05',
    emoji: '🧑‍💼',
    title: 'The Intern Army',
    lead: 'Unpaid. Unqualified. Unstoppable.',
    body: 'The community grew — not as investors, but as Floobiz Interns. They didn\'t follow charts. They followed chaos. Every dip became a joke. Every pump a celebration. Every moment a meme.',
  },
  {
    num: '06',
    emoji: '🔥',
    title: 'Controlled Chaos',
    lead: 'Floobiz became something bigger than a token.',
    body: 'A rebellion against fake expertise. A parody of "serious" crypto. A place where failure wasn\'t feared — it was fuel. The headquarters? Always on fire. The strategy? Questionable at best. The results? Unexplainably positive.',
  },
];

const MARQUEE_WORDS = [
  'NO ROADMAP',
  'NO PROMISES',
  'NO IDEA',
  'NO CLUE',
  'NO PLAN',
  'NO PROBLEM',
  'WE KNOW NOTHING',
  'WE\'RE WINNING',
];

function Landing() {
  return (
    <div className="landing">
      <header className="landing-nav">
        <div className="landing-nav__brand">
          <img src={floobizMark} alt={SITE_NAME} />
          <span>{SITE_NAME}</span>
        </div>
        <nav>
          <a href="#story" className="landing-link">The Story</a>
          <a href="#interns" className="landing-link">Interns</a>
          <Link to="/buy" className="landing-cta">Buy ${TOKEN_SYMBOL}</Link>
          <SignedOut>
            <Link to="/sign-in" className="landing-link">Sign in</Link>
          </SignedOut>
        </nav>
      </header>

      <section className="landing-hero">
        <div className="landing-hero__bg" aria-hidden="true">
          <span className="blob blob--green" />
          <span className="blob blob--gold" />
          <span className="blob blob--cyan" />
        </div>

        <div className="landing-hero__text">
          <div className="landing-hero__badge">
            <span className="dot" /> Live on BNB Chain — Presale OPEN
          </div>
          <h1>
            <span className="h1-line">The dumbest</span>
            <span className="h1-line h1-line--accent">business</span>
            <span className="h1-line">on the blockchain.</span>
          </h1>
          <p className="landing-hero__tagline">
            No vision. No innovation. No idea. Just a mistake that refused to fail —
            and somehow turned into <strong>${TOKEN_SYMBOL}</strong>.
          </p>
          <div className="landing-hero__ctas">
            <Link to="/buy" className="landing-cta landing-cta--primary">
              Buy ${TOKEN_SYMBOL} now →
            </Link>
            <a href="#story" className="landing-cta landing-cta--ghost">
              Read the story
            </a>
          </div>
          <div className="landing-hero__chips">
            <span className="chip">🟢 BEP-20</span>
            <span className="chip">⚡ Low gas</span>
            <span className="chip">🦊 MetaMask</span>
            <span className="chip">🪙 USDT priced</span>
          </div>
        </div>

        <div className="landing-hero__art">
          <div className="landing-hero__art-frame">
            <img src={floobizHero} alt={`${SITE_NAME} mascot`} />
            <div className="speech-bubble">
              "I have no idea what I'm doing.<br/>
              <em>— and it's working.</em>"
            </div>
          </div>
        </div>
      </section>

      <div className="marquee" aria-hidden="true">
        <div className="marquee__track">
          {[...MARQUEE_WORDS, ...MARQUEE_WORDS, ...MARQUEE_WORDS].map((w, i) => (
            <span key={i} className="marquee__item">
              {w} <span className="marquee__sep">★</span>
            </span>
          ))}
        </div>
      </div>

      <section className="landing-stats">
        <div className="stat">
          <span className="stat__num">0</span>
          <span className="stat__label">Roadmaps published</span>
        </div>
        <div className="stat">
          <span className="stat__num">∞</span>
          <span className="stat__label">Bad decisions made</span>
        </div>
        <div className="stat">
          <span className="stat__num">100%</span>
          <span className="stat__label">Vibes-based strategy</span>
        </div>
        <div className="stat">
          <span className="stat__num">1</span>
          <span className="stat__label">CEO (slightly confused)</span>
        </div>
      </section>

      <section id="story" className="landing-story">
        <div className="landing-section__head">
          <p className="eyebrow">Floobiz: The Story</p>
          <h2>In a market obsessed with genius, <em>Floobiz was born by accident.</em></h2>
        </div>

        <div className="chapters">
          {CHAPTERS.map((c) => (
            <article key={c.num} className="chapter">
              <header className="chapter__head">
                <span className="chapter__num">{c.num}</span>
                <span className="chapter__emoji" aria-hidden="true">{c.emoji}</span>
                <h3 className="chapter__title">{c.title}</h3>
              </header>
              <p className="chapter__lead">{c.lead}</p>
              <p className="chapter__body">{c.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="interns" className="landing-manifesto">
        <div className="manifesto-card">
          <p className="eyebrow eyebrow--dark">Join the Intern Army</p>
          <h2>Unpaid. Unqualified. <span className="strike">Unstoppable.</span></h2>
          <p className="manifesto-body">
            Floobiz Interns don't follow charts. They follow chaos. They turn every
            dip into a joke, every pump into a celebration, and every Monday into a
            problem for future Mr. Floobiz.
          </p>
          <ul className="manifesto-list">
            <li>🚫 No qualifications required</li>
            <li>🎓 No experience preferred</li>
            <li>📉 Losses celebrated like wins</li>
            <li>🔥 HQ is always on fire (this is fine)</li>
          </ul>
          <Link to="/buy" className="landing-cta landing-cta--primary">
            Become an Intern →
          </Link>
        </div>
      </section>

      <section className="landing-features">
        <div className="landing-section__head">
          <p className="eyebrow">How it works (somehow)</p>
          <h2>Buying ${TOKEN_SYMBOL} is the only part we got right.</h2>
        </div>
        <div className="feature-grid">
          <div className="landing-feature">
            <div className="landing-feature__icon">🦊</div>
            <h3>Connect</h3>
            <p>Open MetaMask. Switch to BNB Chain. Pretend you know what you're doing.</p>
          </div>
          <div className="landing-feature">
            <div className="landing-feature__icon">✅</div>
            <h3>Approve</h3>
            <p>One USDT approval. Sign it. The contract is fixed-rate — no surprises.</p>
          </div>
          <div className="landing-feature">
            <div className="landing-feature__icon">🚀</div>
            <h3>Buy</h3>
            <p>Tokens land in your wallet instantly. You are now an Intern. Welcome.</p>
          </div>
        </div>
      </section>

      <section className="landing-final">
        <h2 className="landing-final__title">
          We promise <span className="strike">everything</span> nothing.
        </h2>
        <p className="landing-final__body">
          No roadmap. No guarantees. No illusion of control.<br/>
          Just one simple idea:
        </p>
        <p className="landing-final__quote">
          "We know nothing… <span className="accent">but we're winning.</span>"
        </p>
        <Link to="/buy" className="landing-cta landing-cta--giant">
          Buy ${TOKEN_SYMBOL} →
        </Link>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer__row">
          <div className="landing-footer__brand">
            <img src={floobizMark} alt="" />
            <span>{SITE_NAME}</span>
          </div>
          <p>© {new Date().getFullYear()} {SITE_NAME}. We know nothing, but we're winning.</p>
        </div>
        <p className="landing-footer__fine">
          Not financial advice. Probably not advice at all. Floobiz is a meme token —
          assume total loss before you assume anything else.
        </p>
      </footer>
    </div>
  );
}

export default Landing;
